import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { LOGO_PNG_BASE64, LOGO_ASPECT_RATIO } from "./logoData";

const LIME = [166, 226, 46];
const BLACK = [10, 10, 10];

function header(doc, teacher) {
  doc.setFillColor(...BLACK);
  doc.rect(0, 0, 210, 22, "F");
  doc.setFillColor(...LIME);
  doc.rect(0, 22, 210, 1.2, "F");

  const logoH = 14;
  const logoW = logoH * LOGO_ASPECT_RATIO;
  doc.addImage(LOGO_PNG_BASE64, "PNG", 14, 4, logoW, logoH);

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("[THE] TEACHKIT", 14 + logoW + 4, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(200, 200, 200);
  const teacherLine = [teacher?.full_name, teacher?.school_name, teacher?.school_city]
    .filter(Boolean)
    .join(" • ");
  if (teacherLine) doc.text(teacherLine, 196, 13, { align: "right" });
}

function footer(doc, pageNum, total) {
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(`Page ${pageNum} / ${total}`, 196, 290, { align: "right" });
  doc.text("Generated by THE TEACHKIT", 14, 290);
}

export function exportLessonPdf(lesson, teacher) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(doc, teacher);

  // Title
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text(`Week ${lesson.week}`, 14, 40);
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(`CEFR ${lesson.cefr_level || "A1-A2"}`, 14, 46);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...BLACK);
  doc.text(lesson.title || lesson.theme, 14, 56, { maxWidth: 180 });

  // Meta table
  autoTable(doc, {
    startY: 65,
    theme: "grid",
    head: [["Field", "Details"]],
    body: [
      ["Grammar focus", lesson.grammar || "—"],
      ["Vocabulary", (lesson.vocabulary || []).join(", ")],
      ["Objectives", (lesson.objectives || []).map((o, i) => `${i + 1}. ${o}`).join("\n")],
    ],
    headStyles: { fillColor: BLACK, textColor: 255, fontStyle: "bold" },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: { 0: { cellWidth: 40, fontStyle: "bold" }, 1: { cellWidth: 142 } },
  });

  // Materials
  let y = doc.lastAutoTable.finalY + 6;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...BLACK);
  doc.text("MATERIALS", 14, y);
  doc.setDrawColor(...LIME);
  doc.setLineWidth(0.8);
  doc.line(14, y + 1.5, 40, y + 1.5);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  (lesson.materials || []).forEach((m) => {
    doc.text(`• ${m}`, 16, y);
    y += 5;
  });

  // PPP
  const phases = ["presentation", "practice", "production"];
  phases.forEach((p) => {
    const phase = lesson.ppp?.[p];
    if (!phase) return;
    if (y > 240) { doc.addPage(); header(doc, teacher); y = 40; }
    y += 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...BLACK);
    doc.text(`${phase.phase.toUpperCase()}   ·   ${phase.duration_min} min`, 14, y);
    doc.setDrawColor(...LIME);
    doc.line(14, y + 1.5, 60, y + 1.5);
    y += 6;
    autoTable(doc, {
      startY: y,
      theme: "striped",
      head: [["#", "Activity", "Type", "Skill", "Description"]],
      body: (phase.activities || []).map((a, i) => [i + 1, a.title, a.type, a.skill, a.description]),
      headStyles: { fillColor: LIME, textColor: BLACK, fontStyle: "bold" },
      styles: { fontSize: 8, cellPadding: 2.5, valign: "top" },
      columnStyles: {
        0: { cellWidth: 8 },
        1: { cellWidth: 42, fontStyle: "bold" },
        2: { cellWidth: 24 },
        3: { cellWidth: 20 },
        4: { cellWidth: 88 },
      },
    });
    y = doc.lastAutoTable.finalY + 2;
  });

  if (lesson.notes) {
    if (y > 250) { doc.addPage(); header(doc, teacher); y = 40; }
    y += 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("TEACHER NOTES", 14, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const split = doc.splitTextToSize(lesson.notes, 180);
    doc.text(split, 14, y);
  }

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    footer(doc, i, pageCount);
  }

  doc.save(`teachkit-week-${lesson.week}.pdf`);
}

// class_type-aware subtitle — the old hardcoded "CECRL A1-A2 · Ages 10-12"
// was actively wrong once Adult/Business tracks were added (an adult
// professional's syllabus shouldn't say "Ages 10-12").
function syllabusSubtitle(syllabus) {
  const level = syllabus?.class_level || "";
  if (syllabus?.class_type === "adult") return `Adult General English · CEFR ${level}`;
  if (syllabus?.class_type === "business") return `Business English · CEFR ${level}`;
  return `Aligned with the French National Curriculum · Class: ${level}`;
}

export function exportSyllabusPdf(syllabus, teacher) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(doc, teacher);

  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.text("32-Week English Syllabus", 14, 42);
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  doc.text(syllabusSubtitle(syllabus), 14, 49);

  autoTable(doc, {
    startY: 58,
    head: [["Wk", "Theme", "CEFR", "Grammar focus", "Objectives"]],
    body: syllabus.lessons.map((l) => [
      l.week,
      l.title,
      l.cefr_level,
      l.grammar,
      (l.objectives || []).slice(0, 2).map((o) => `• ${o}`).join("\n"),
    ]),
    headStyles: { fillColor: BLACK, textColor: 255 },
    styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: {
      0: { cellWidth: 10 },
      1: { cellWidth: 43, fontStyle: "bold" },
      2: { cellWidth: 12 },
      3: { cellWidth: 47 },
      4: { cellWidth: 70 },
    },
    didDrawPage: () => header(doc, teacher),
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    footer(doc, i, pageCount);
  }

  doc.save(`teachkit-syllabus.pdf`);
}
